/**
 * Notification Center (v109 §7, prototype v108). Flags are department-owned work items (today's flagged_accounts);
 * System notifications are alerts (today's role_notifications). One feed, newest first, with All / Flags / System,
 * Admin's Show all / Admin only, search and a date range. Opening a row marks it read for this viewer and opens the
 * record; it never resolves the Flag. Resolve is its own button, for Admin or the owning department. Clear is the
 * existing action (it also cancels linked reminders), kept explicit.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, Loader2, CheckCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/admin/ui/badge';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { fetchInBatches } from '@utils/batchSupabaseQueries';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { systemItem, staffItem, flagItem, visibleTo, filterItems, sortNewest, counts, canResolve, formatWhen, readMark } from '@/lib/notifications/center';
import { useNotificationReads } from '../useNotificationReads';
import { flagFilter } from '@/lib/notifications/mentions';
import FlagDialog from '../components/FlagDialog';
import { stagingFetch } from '../api';
import { ROLE_LABELS, STAGING_ROOT } from '@/lib/staging/roleNav';
import { useStaging } from '../StagingLayout';

const KINDS = [{ key: 'all', label: 'All Notifications' }, { key: 'flag', label: 'Flags' }, { key: 'system', label: 'System' }];
const FLAG_STATUSES = [{ key: 'unread', label: 'Unread' }, { key: 'active', label: 'Active' }, { key: 'resolved', label: 'Resolved' }, { key: 'all', label: 'All' }];

export default function NotificationCenter() {
  const { role, actualRole } = useStaging();
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // A department's "Open Flags" lands here scoped to that department's flags (prototype v108 P 1473): ?kind=flag&department=billing
  const deptScope = params.get('department');
  const [kind, setKind] = useState<'all' | 'flag' | 'system'>(() => (['flag', 'system'].includes(params.get('kind') || '') ? params.get('kind') as 'flag' | 'system' : 'all'));
  const [systemStatus, setSystemStatus] = useState<'unread' | 'all'>('all');
  const [adminScope, setAdminScope] = useState<'all' | 'admin'>('all');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [raw, setRaw] = useState<{ system: any[]; flags: any[]; bookings: Map<string, any>; people: Map<string, string> } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { marks: readMarks, markRead, source: readSource } = useNotificationReads(user?.id);
  const [busy, setBusy] = useState<string | null>(null);
  const [flagStatus, setFlagStatus] = useState<'unread' | 'active' | 'resolved' | 'all'>('active');
  const [openFlag, setOpenFlag] = useState<string | null>(null);
  const [mentions, setMentions] = useState<any[]>([]);
  const [feed, setFeed] = useState<{ installed: boolean; items: any[] }>({ installed: false, items: [] });

  const load = async () => {
    try {
      const [n, f] = await Promise.all([
        supabaseAdmin.from('role_notifications').select('id, account_name, severity, type, created_at, target_role, message, booking_id, status').order('created_at', { ascending: false }).limit(300),
        supabaseAdmin.from('flagged_accounts').select('id, user_id, reason, notes, severity, status, assigned_to, assigned_to_role, flagged_by, created_at, resolved_at').order('created_at', { ascending: false }).limit(200)
      ]);
      if (n.error) throw n.error; if (f.error) throw f.error;
      const bookingIds = [...new Set((n.data || []).map((r: any) => r.booking_id).filter(Boolean))];
      const peopleIds = [...new Set((f.data || []).flatMap((r: any) => [r.user_id, r.assigned_to, r.flagged_by]).filter(Boolean))];
      const db = supabaseAdmin as any;
      const [b, p] = await Promise.all([
        bookingIds.length ? fetchInBatches(db, 'bookings', 'id, hours_booked, personalization_data', 'id', bookingIds) : Promise.resolve({ data: [] as any[] }),
        peopleIds.length ? fetchInBatches(db, 'users', 'id, full_name', 'id', peopleIds) : Promise.resolve({ data: [] as any[] })
      ]);
      setRaw({ system: n.data || [], flags: f.data || [], bookings: new Map((b.data || []).map((x: any) => [x.id, x])), people: new Map((p.data || []).map((x: any) => [x.id, x.full_name])) });
      setError(null);
    } catch (e: any) { setError(e.message || String(e)); }
  };
  const loadMentions = () => stagingFetch('/api/admin/staging/mentions').then((d) => setMentions(d.items || [])).catch(() => setMentions([]));
  const loadFeed = () => stagingFetch('/api/admin/staging/notifications/feed').then((d) => setFeed({ installed: Boolean(d.installed), items: d.items || [] })).catch(() => setFeed({ installed: false, items: [] }));
  useEffect(() => { load(); loadMentions(); loadFeed(); const t = setInterval(() => { load(); loadMentions(); loadFeed(); }, 30000); return () => clearInterval(t); }, []);

  const items = useMemo(() => {
    if (!raw) return [];
    const all = [
      ...raw.system.map((r: any) => systemItem(r, { booking: raw.bookings.get(r.booking_id) || null, read: readMark(readMarks, `system:${r.id}`, r.created_at) })),
      ...raw.flags.map((r: any) => ({ ...flagItem(r, { people: raw.people }), read: flagItem(r, { people: raw.people }).read || readMarks.has(`flag:${r.id}`) }))
    ];
    // The new console's own notifications (Traci's list); personal ones (flags, mentions) are already only mine.
    const staff = feed.items.map((r: any) => staffItem(r, { read: readMark(readMarks, `system:${r.id}`, r.created_at) }));
    // Mentions are personal: "you were tagged in a flag" shows for the person tagged, whatever the department.
    // Once the notification table is installed its @Mention rows replace this list.
    const mine = feed.installed ? [] : mentions.map((m: any) => ({
      id: `system:${m.id}`, sourceId: m.id, kind: 'system', type: 'flag_mention', title: 'Mentioned in a flag',
      message: `${m.author_name || 'Someone'}: ${m.body}`, accountName: m.client_name, routedLabel: m.flag_reason ? `flag: ${m.flag_reason}` : null,
      severity: 'medium', status: 'unread', read: readMarks.has(`system:${m.id}`), at: m.created_at, flag_id: m.flag_id
    }));
    const scoped = [...visibleTo([...all, ...staff], role, { adminScope }), ...mine];
    const filtered = filterItems(scoped, { kind, search, from, to }).filter((it: any) => !deptScope || kind !== 'flag' || it.ownerRole === deptScope);
    if (kind === 'flag') return sortNewest(flagFilter(filtered, flagStatus));
    if (kind === 'system' && systemStatus === 'unread') return sortNewest(filtered.filter((it: any) => !it.read));
    return sortNewest(filtered);
  }, [raw, role, adminScope, kind, search, from, to, readMarks, mentions, flagStatus, feed, deptScope, systemStatus]);
  const showAllFlags = () => { const next = new URLSearchParams(params); next.delete('department'); setParams(next, { replace: true }); };
  const c = useMemo(() => counts(items), [items]);

  // Opening marks read and never resolves (§7): a flag or a mention opens the flag; an alert opens its booking.
  const open = (it: any) => {
    markRead([it.id]);
    if (it.kind === 'flag') setOpenFlag(it.sourceId);
    else if (it.flag_id) setOpenFlag(it.flag_id);
    else if (it.to?.booking) navigate(`${STAGING_ROOT}/bookings/${it.to.booking}`);
    else if (it.to?.client) navigate(`${STAGING_ROOT}/clients/${it.to.client}`);
  };
  // From a bell preview (?open=<item id>): mark it read, open its flag, and point at its row (Traci, 28 Sep 2026)
  const [focusId, setFocusId] = useState<string | null>(null);
  useEffect(() => {
    const want = params.get('open');
    if (!want || !raw) return;
    const it = items.find((x: any) => x.id === want);
    const next = new URLSearchParams(params); next.delete('open'); setParams(next, { replace: true });
    if (!it) return;
    markRead([it.id]);
    if (it.kind === 'flag') setOpenFlag(it.sourceId);
    else if (it.flag_id) setOpenFlag(it.flag_id);
    setFocusId(it.id);
    requestAnimationFrame(() => document.getElementById(`note-${it.id}`)?.scrollIntoView({ block: 'center' }));
  }, [raw, items, params]); // eslint-disable-line react-hooks/exhaustive-deps
  const markAllRead = () => { markRead(items.map((it: any) => it.id)); toast.success(`${items.length} marked read for you`); };

  const clearSystem = async (it: any) => {
    if (!window.confirm('Clear this notification? Clearing also cancels any reminder text linked to it and stops the automation re-sending the same one. Use "open" if you only want to mark it read.')) return;
    setBusy(it.id);
    try {
      const { data: { session } } = await supabaseAdmin.auth.getSession();
      const res = await fetch(`/api/notifications/${it.sourceId}/clear`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` }, body: JSON.stringify({ userId: session?.user?.id }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      toast.success('Cleared'); await load();
    } catch (e: any) { toast.error(`Could not clear: ${e.message || e}`); } finally { setBusy(null); }
  };

  return (
    <div className="p-8 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">Notification Center</h2>
          <p className="text-sm text-muted-foreground mt-1">Flags are department-owned work items. System notifications are alerts. The combined view shows both together, newest first.</p>
        </div>
        <Button variant="outline" size="sm" onClick={markAllRead} disabled={!items.length}><CheckCheck className="h-4 w-4 mr-1" /> Mark All Read</Button>
      </div>

      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Kind">
        {KINDS.map((k) => <button key={k.key} type="button" role="tab" aria-selected={kind === k.key} onClick={() => setKind(k.key as any)} className={`rounded-full border px-3 py-1 text-xs ${kind === k.key ? 'bg-primary text-primary-foreground border-primary' : 'bg-card'}`}>{k.label}{k.key === 'flag' && c.openFlags ? ` · ${c.openFlags} open` : ''}</button>)}
      </div>
      {kind === 'flag' && deptScope && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
          <span>Showing <b>{ROLE_LABELS[deptScope] || deptScope}</b> Flags only.</span>
          <Button size="sm" variant="outline" onClick={showAllFlags}>Show All Flags</Button>
        </div>
      )}
      {kind === 'system' && (
        <div className="flex gap-1.5" role="tablist" aria-label="System status">
          {(['unread', 'all'] as const).map((k) => <button key={k} type="button" role="tab" aria-selected={systemStatus === k} onClick={() => setSystemStatus(k)} className={`rounded-full border px-3 py-1 text-xs ${systemStatus === k ? 'bg-accent' : 'bg-card'}`}>{k === 'unread' ? 'Unread' : 'All'}</button>)}
        </div>
      )}
      {kind === 'flag' && (
        <div className="flex gap-1.5" role="tablist" aria-label="Flag status">
          {FLAG_STATUSES.map((t) => <button key={t.key} type="button" role="tab" aria-selected={flagStatus === t.key} onClick={() => setFlagStatus(t.key as any)} className={`rounded-full border px-3 py-1 text-xs ${flagStatus === t.key ? 'bg-accent' : 'bg-card'}`}>{t.label}</button>)}
        </div>
      )}
      {actualRole === 'admin' && role === 'admin' && (
        <div className="flex gap-1.5" role="tablist" aria-label="Scope">
          <button type="button" role="tab" aria-selected={adminScope === 'all'} onClick={() => setAdminScope('all')} className={`rounded-full border px-3 py-1 text-xs ${adminScope === 'all' ? 'bg-accent' : 'bg-card'}`}>Show All Notifications</button>
          <button type="button" role="tab" aria-selected={adminScope === 'admin'} onClick={() => setAdminScope('admin')} className={`rounded-full border px-3 py-1 text-xs ${adminScope === 'admin' ? 'bg-accent' : 'bg-card'}`}>Show Admin Only</button>
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-[2fr_1fr_1fr]">
        <div><label className="text-[11px] uppercase tracking-wide text-muted-foreground">Search</label><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client, talent, booking, ID or text…" /></div>
        <div><label className="text-[11px] uppercase tracking-wide text-muted-foreground">From date</label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><label className="text-[11px] uppercase tracking-wide text-muted-foreground">To date</label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {!raw && !error && <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}

      {raw && (
        <div className="rounded-xl border bg-card divide-y">
          {items.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">Nothing matches.</p> : items.map((it: any) => (
            <div key={it.id} id={`note-${it.id}`} className={`grid grid-cols-[92px_minmax(0,1fr)_120px_28px] gap-4 px-4 py-3 items-start ${it.read ? 'bg-muted/40' : ''} ${focusId === it.id ? 'ring-2 ring-primary ring-inset' : ''}`}>
              <div className="space-y-1">
                <Badge variant="outline" className="text-[10px] tracking-wide">{it.kind === 'flag' ? 'FLAG' : 'SYSTEM'}</Badge>
                {it.kind === 'flag' && <Badge variant="outline" className={`block w-fit text-[10px] tracking-wide ${it.flagStatus === 'resolved' ? 'text-emerald-800 border-emerald-300' : it.flagStatus === 'escalated' || it.overdue ? 'text-red-800 border-red-300' : 'text-amber-800 border-amber-300'}`}>{it.flagStatusLabel}</Badge>}
                {it.kind === 'system' && it.severity === 'high' && !it.read && <Badge variant="outline" className="block w-fit text-[10px] text-red-800 border-red-300">HIGH</Badge>}
              </div>
              <button type="button" onClick={() => open(it)} className="text-left min-w-0">
                <div className={`text-sm ${it.read ? 'font-medium text-muted-foreground' : 'font-semibold'}`}>{it.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{it.message}</div>
                <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-x-2">
                  {it.accountName && <b className="text-foreground/80">{it.accountName}</b>}
                  {it.packageText && <span>· {it.packageText}</span>}
                  {it.bookingId && <span className="tabular-nums">· {String(it.bookingId).slice(0, 8)}</span>}
                  {it.routedLabel && <span className="text-sky-800">· {it.routedLabel}</span>}
                  {it.flaggedBy && <span>· flagged by {it.flaggedBy}</span>}
                </div>
              </button>
              <div className="text-[11px] text-muted-foreground tabular-nums text-right">{formatWhen(it.at)}
                <div className="mt-1 flex justify-end gap-2">
                  {canResolve(it, role) && <button type="button" onClick={() => open(it)} className="underline text-emerald-800">Resolve</button>}
                  {/* Traci's doc, Oct 2026: a Read button beside Clear; it marks the notification read for you only */}
                  {it.kind === 'system' && !it.read && <button type="button" onClick={() => markRead([it.id])} className="underline" title="Marks it read for you only; everyone else still sees it unread">Read</button>}
                  {it.kind === 'system' && !it.staff && it.status !== 'cleared' && <button type="button" disabled={busy === it.id} onClick={() => clearSystem(it)} className="underline" title="Clears the notification and cancels any reminder text linked to it">Clear</button>}
                </div>
              </div>
              <button type="button" onClick={() => open(it)} aria-label="Open" className="text-muted-foreground hover:text-foreground mt-1"><ChevronRight className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        {readSource === 'browser' ? 'Read marks are being kept in this browser only: the notification_reads migration has not run yet. ' : 'Read marks are personal to you and follow you between devices. '}
        Clear and Resolve are recorded for everyone. Open a flag for its discussion; @mention someone to bring them in.
      </p>
      <FlagDialog flagId={openFlag} onClose={() => setOpenFlag(null)} onChanged={() => { load(); loadMentions(); }} />
    </div>
  );
}
